import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { X, Loader2, AlertCircle, Search, ShieldCheck, CalendarClock } from "lucide-react";

/**
 * Cloud Audit Log (Modal)
 * -----------------------
 * Zentraler Nextcloud-admin_audit-Trail der Cloud aus ClickHouse.
 * Die VMs liefern ihre LOKALEN audit.log-Dateien (Datadir ist pro VM, nicht
 * NFS-geteilt) über den Agent-Log-Shipper — ClickHouse merged sie mit
 * role/vmid-Anreicherung zu einer Cloud-weiten Sicht.
 *
 * Enthält die Aufbewahrungsregel (Retention): pro Cloud in Tagen (1..90,
 * Default aus CM_AUDIT_RETENTION_DAYS). Gilt für NEUE Zeilen (Per-Row-TTL).
 */
const MAX_DAYS = 90;
const POLL_MS = 10000;

const fetcher = (u) => api.get(u).then((r) => r.data);

function fmtTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 19).replace("T", " ") || "—";
    return d.toLocaleString("en-US", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
}

export default function CloudAuditLogModal({ cloudId, cloudName, onClose }) {
    const [q, setQ] = useState("");
    const [user, setUser] = useState("");
    const [ret, setRet] = useState(null);
    const [retErr, setRetErr] = useState(null);
    const [retSaving, setRetSaving] = useState(false);

    const qs = new URLSearchParams({ cloud_id: cloudId, limit: "300" });
    if (q.trim()) qs.set("q", q.trim());
    if (user.trim()) qs.set("user", user.trim());

    const { data, error, isLoading } = useSWR(
        `/api/admin/logs/audit?${qs}`,
        fetcher,
        { refreshInterval: POLL_MS, dedupingInterval: 3000 },
    );

    // Retention laden (einmalig beim Öffnen).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/api/admin/logs/audit-retention/${cloudId}`);
                if (!cancelled) setRet(r.data);
            } catch (e) {
                if (!cancelled) setRetErr(formatApiError(e));
            }
        })();
        return () => { cancelled = true; };
    }, [cloudId]);

    // Escape schließt.
    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const saveRetention = async (days) => {
        setRetSaving(true);
        setRetErr(null);
        try {
            const r = await api.put(`/api/admin/logs/audit-retention/${cloudId}`, { days });
            setRet(r.data);
        } catch (e) {
            setRetErr(formatApiError(e));
        } finally {
            setRetSaving(false);
        }
    };

    const items = data?.items || [];
    const enabled = data?.enabled !== false;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="w-full max-w-5xl max-h-[88vh] flex flex-col rounded-xl border border-zinc-800 bg-[#0B0E14] shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-5 py-3.5 border-b border-zinc-800/80 shrink-0">
                    <ShieldCheck size={18} className="text-cm-brand" />
                    <div className="min-w-0">
                        <h2 className="text-sm font-mono font-semibold text-zinc-100 truncate">
                            Audit log — {cloudName || cloudId.slice(0, 8)}
                        </h2>
                        <p className="text-xs text-zinc-500 font-mono">
                            Nextcloud admin_audit · central (ClickHouse) · worker + web VMs merged
                        </p>
                    </div>

                    {/* Retention */}
                    <div className="ml-auto flex items-center gap-2" title="Retention of new audit rows (days, max 90)">
                        <CalendarClock size={14} className="text-zinc-500" />
                        <label className="text-xs font-mono text-zinc-500">Retention:</label>
                        <select
                            data-testid="audit-retention-select"
                            className="cm-input !w-auto !py-1 text-xs font-mono"
                            value={ret?.days ?? ""}
                            disabled={retSaving || !ret}
                            onChange={(e) => {
                                const d = parseInt(e.target.value, 10);
                                if (!Number.isNaN(d)) saveRetention(d);
                            }}>
                            {!ret && <option value="">…</option>}
                            {Array.from({ length: MAX_DAYS }, (_, i) => i + 1).map((d) => (
                                <option key={d} value={d}>{d} {d === 1 ? "day" : "days"}</option>
                            ))}
                        </select>
                        {ret?.default_days != null && (
                            <span className="text-[11px] font-mono text-zinc-600">Default {ret.default_days}d</span>
                        )}
                    </div>

                    <button data-testid="audit-modal-close" onClick={onClose}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 shrink-0" title="Close (Esc)">
                        <X size={16} />
                    </button>
                </div>

                {/* Filter */}
                <div className="flex items-center gap-2 px-5 py-2.5 border-b border-zinc-800/60 shrink-0">
                    <Search size={14} className="text-zinc-500 shrink-0" />
                    <input
                        data-testid="audit-search"
                        className="cm-input flex-1 max-w-md font-mono text-xs"
                        placeholder="Full-text search (e.g. config, password, user)"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <input
                        data-testid="audit-user-filter"
                        className="cm-input max-w-[200px] font-mono text-xs"
                        placeholder="NC user (optional)"
                        value={user}
                        onChange={(e) => setUser(e.target.value)}
                    />
                    <div className="ml-auto text-xs font-mono text-zinc-500">
                        {isLoading ? "loading…" : `${items.length} entries`}
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-auto min-h-0">
                    {retErr && (
                        <div className="m-4 flex items-center gap-2 text-xs font-mono text-amber-300 bg-amber-950/30 border border-amber-900/60 rounded-lg px-3 py-2">
                            <AlertCircle size={14} className="shrink-0" /> {retErr}
                        </div>
                    )}
                    {isLoading ? (
                        <div className="flex items-center justify-center gap-2 py-16 text-zinc-500 font-mono text-sm">
                            <Loader2 size={16} className="animate-spin" /> Loading audit log …
                        </div>
                    ) : error ? (
                        <div className="m-4 flex items-center gap-2 text-xs font-mono text-red-300 bg-red-950/30 border border-red-900/60 rounded-lg px-3 py-2">
                            <AlertCircle size={14} className="shrink-0" /> {formatApiError(error)}
                        </div>
                    ) : !enabled ? (
                        <div className="m-4 text-xs font-mono text-zinc-500">
                            Central logging is disabled (ClickHouse unreachable) — audit log not available.
                        </div>
                    ) : !items.length ? (
                        <div className="py-16 text-center text-zinc-500 font-mono text-sm">
                            No audit entries in the time window
                            {q.trim() || user.trim() ? " for this filter" : ""}.
                        </div>
                    ) : (
                        <table className="w-full text-left font-mono text-xs">
                            <thead className="sticky top-0 bg-[#0B0E14] border-b border-zinc-800/80 text-zinc-500">
                                <tr>
                                    <th className="px-4 py-2 font-medium w-[130px]">Time</th>
                                    <th className="px-2 py-2 font-medium w-[90px]">VM</th>
                                    <th className="px-2 py-2 font-medium w-[130px]">User</th>
                                    <th className="px-2 py-2 font-medium">Action / Message</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-800/40">
                                {items.map((e, i) => (
                                    <tr key={`${e.ts}-${i}`} className="hover:bg-zinc-900/40 align-top"
                                        data-testid={`audit-row-${i}`}>
                                        <td className="px-4 py-1.5 text-zinc-400 whitespace-nowrap">{fmtTime(e.ts)}</td>
                                        <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap">
                                            {e.role}:{e.vmid || "?"}
                                        </td>
                                        <td className="px-2 py-1.5 text-sky-300 whitespace-nowrap">{e.user || "—"}</td>
                                        <td className="px-2 py-1.5 text-zinc-200 break-words">
                                            {e.action && e.action !== e.message ? (
                                                <span className="text-[#CCFF00]/90">{e.action}</span>
                                            ) : null}
                                            <span className="text-zinc-300"> {e.message}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
